import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "spacer"
}
const _hoisted_2 = {
  key: 1,
  class: "configurations"
}

import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { sortBy } from '@shell/utils/sort';
import { _VIEW } from '@shell/config/query-params';
import { EpinioConfiguration, EpinioService, EPINIO_TYPES, EPINIO_APP_MANIFEST, EpinioAppBindings } from '../../types';
import Application from '../../models/applications';
import ResourceDropdown from './ResourceDropdown.vue';

interface Props {
  initialApplication?: Application;
  application: Application;
  mode: string;
  bindings?: EpinioAppBindings;
  active: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppConfiguration',
  props: {
    initialApplication: {},
    application: {},
    mode: {},
    bindings: {},
    active: { type: Boolean }
  },
  emits: ['change', 'initial'],
  setup(__props: any, { emit: __emit }) {


const props = __props;
// 'initial' reports what was bound when the form opened, so the parent can diff
// against it on save instead of re-deriving it from the store.
const emit = __emit;

const store = useStore();

const t = store.getters['i18n/t'];

const values = ref({
  configurations: props.bindings?.configurations || [] as string[],
  services: props.bindings?.services.map((s: EpinioService) => `${props.application.metadata.namespace}/${s.meta.name}`) || [] as string[],
});

const isLoadingConfigurations = ref(false);
const fetchedConfigurations = ref<any[]>([]);
const cachedConfigurations = ref<any[]>([]);

const isLoadingServices = ref(false);
const fetchedServices = ref<any[]>([]);
const cachedServices = ref<any[]>([]);

const isFetchingConfigsAndServices = ref<boolean>(true);

const hasConfigs = ref<boolean>(false);
const noConfigs = computed(() => !hasConfigs.value);
const hasServices = ref<boolean>(false);
const noServices = computed(() => !hasServices.value);

const fetchConfigurations = async () => {
  if (cachedConfigurations.value.length > 0) {
    fetchedConfigurations.value = cachedConfigurations.value;
    return;
  }
  isLoadingConfigurations.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/namespaces/${props.application.metadata.namespace}/configurations`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.CONFIGURATION, ...item })
    ));
    fetchedConfigurations.value = classifiedData;
    cachedConfigurations.value = classifiedData;
    hasConfigs.value = classifiedData.length > 0;
  } catch (error) {
    console.error('Failed to fetch configurations', error);
  } finally {
    isLoadingConfigurations.value = false;
  }
};

async function searchConfigurations(query: string) {
  isLoadingConfigurations.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/namespaces/${props.application.metadata.namespace}/configurations?search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.CONFIGURATION, ...item })
    ));
    fetchedConfigurations.value = classifiedData;
  } catch {
    fetchedConfigurations.value = [];
  } finally {
    isLoadingConfigurations.value = false;
  }
}

const fetchServices = async () => {
  if (cachedServices.value.length > 0) {
    fetchedServices.value = cachedServices.value;
    return;
  }
  isLoadingServices.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/namespaces/${props.application.metadata.namespace}/services`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];

    // classify raw JSON into proper model instances (adds bindServices, etc.)
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.SERVICE_INSTANCE, ...item })
    ));
    fetchedServices.value = classifiedData;
    cachedServices.value = classifiedData;
    hasServices.value = classifiedData.length > 0;
  } catch (error) {
    console.error('Failed to fetch services', error);
  } finally {
    isLoadingServices.value = false;
  }
};

async function searchServices(query: string) {
  isLoadingServices.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/namespaces/${props.application.metadata.namespace}/services?search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.SERVICE_INSTANCE, ...item })
    ));
    fetchedServices.value = classifiedData;
  } catch {
    fetchedServices.value = [];
  } finally {
    isLoadingServices.value = false;
  }
}

const fetchData = async () => {
  isFetchingConfigsAndServices.value = true;
  await fetchConfigurations();
  await fetchServices();
  isFetchingConfigsAndServices.value = false;
};

watch(() => props.active, (isActive) => {
  if (isActive) {
    fetchData();
  }
  if (!isActive) {
    fetchedConfigurations.value = [];
    fetchedServices.value = [];
    cachedConfigurations.value = [];
    cachedServices.value = [];
  }
}, { immediate: true });

const configurations = computed(() => {
  const list = fetchedConfigurations.value
    .filter((s: EpinioConfiguration) => { return s.configuration.type !== 'service'; })
    .map((s: EpinioConfiguration) => ({
      label: s.meta.name,
      value: s.meta.name,
    }));

  return sortBy(list, 'label', false);
});

const services = computed(() => {
  const list = fetchedServices.value.map((s: EpinioService) => ({
    label: `${s.meta.name} (${s.catalog_service})`,
    value: `${props.application.metadata.namespace}/${s.meta.name}`,
  }));

  return sortBy(list, 'label', false);
});


const isView = computed(() => props.mode === _VIEW);
const isFromManifest = computed(
  () => store.$router.currentRoute._value.query.from === EPINIO_APP_MANIFEST
);

// Watchers
watch(values, () => {
  emit('change', {
    configurations: values.value.configurations,
    services: values.value.services.map((s: string) => fetchedServices.value.find((ns: any) => `${props.application.metadata.namespace}/${ns.meta.name}` === s)),
  })
}, { deep: true });

watch(noConfigs, (neu) => {
  if (neu && values.value.configurations?.length) {
    values.value.configurations = [];
  }
});

watch(noServices, (neu) => {
  if (neu && values.value.services?.length) {
    values.value.services = [];
  }
});

watch(hasConfigs, (neu, old) => {
  if (!old && neu) {
    if (props.initialApplication?.configuration?.configurations) {
      // Bound names come off the app record; the fetched list says which of
      // those are plain configurations rather than service-generated ones.
      const bound = props.initialApplication.configuration.configurations;

      values.value.configurations = fetchedConfigurations.value
        .filter((c: any) => !c.isServiceRelated && bound.includes(c.meta.name))
        .map((c: any) => c.meta.name);

      emit('initial', { configurations: values.value.configurations });
    }

    if (isFromManifest.value) {
      values.value.configurations = fetchedConfigurations.value
        .filter((nc: any) =>
          props.application.configuration.configurations.includes(nc.meta.name) &&
          !nc.isServiceRelated
        )
        .map((nc: any) => nc.meta.name) || [];
    }
  }
}, { immediate: true });

watch(hasServices, (neu, old) => {
    if (!old && neu) {
      if (props.initialApplication?.configuration?.services) {
        const serviceNames = props.initialApplication.configuration.services;
        const bound = fetchedServices.value
          .filter((s: any) => serviceNames.includes(s.meta.name));

        values.value.services = bound
          .map((s: any) => `${props.application.metadata.namespace}/${s.meta.name}`);

        emit('initial', { services: bound });
      }
    }

    if (isFromManifest.value) {
      const configurations = fetchedConfigurations.value
        .filter((nc: any) =>
          props.application.configuration.configurations.includes(nc.meta.name) &&
          nc.isServiceRelated
        );
      values.value.services = fetchedServices.value
        .filter((s: any) => configurations.some((d: any) => s.meta.name === d.configuration.origin))
        .map((elem: any) => `${props.application.metadata.namespace}/${elem.meta.name}`);
    }
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  return (isFetchingConfigsAndServices.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, _cache[0] || (_cache[0] = [
        _createElementVNode("trailhand-loading-spinner", null, null, -1)
      ])))
    : (_openBlock(), _createElementBlock("div", _hoisted_2, [
        _createVNode(ResourceDropdown, {
          values: values.value.configurations,
          options: configurations.value,
          label: _unref(t)('typeLabel.configurations', { count: 2}),
          disabled: noConfigs.value || isView.value,
          filterable: "",
          multiselect: "",
          placeholder: noConfigs.value ? _unref(t)('epinio.applications.steps.configurations.configurations.select.placeholderNoOptions') : _unref(t)('epinio.applications.steps.configurations.configurations.select.placeholderWithOptions'),
          onDropdownChange: (e) => { values.value.configurations = e.detail.values; },
          fetchAllResources: fetchConfigurations,
          searchResources: searchConfigurations,
          isLoading: isLoadingConfigurations.value
        }, null, 8, ["values", "options", "label", "disabled", "placeholder", "onDropdownChange", "isLoading"]),
        _cache[1] || (_cache[1] = _createTextVNode()),
        _createVNode(ResourceDropdown, {
          values: values.value.services,
          options: services.value,
          label: _unref(t)('typeLabel.services', { count: 2}),
          disabled: noServices.value || isView.value,
          filterable: "",
          multiselect: "",
          placeholder: noServices.value ? _unref(t)('epinio.applications.steps.configurations.services.select.placeholderNoOptions') : _unref(t)('epinio.applications.steps.configurations.services.select.placeholderWithOptions'),
          onDropdownChange: (e) => { values.value.services = e.detail.values; },
          fetchAllResources: fetchServices,
          searchResources: searchServices,
          isLoading: isLoadingServices.value
        }, null, 8, ["values", "options", "label", "disabled", "placeholder", "onDropdownChange", "isLoading"])
      ]))
}
}

})